import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Users, ArrowRight, Search, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS_INFO = {
  "tennis": { name: "Tennis", desc: "The world's most popular individual racket sport. Whether you play NTRP 2.5 casual rallies or 5.0 competitive singles, find your perfect match.", skillSystem: "NTRP Rating (1.0–7.0)", formats: ["Singles", "Doubles", "Mixed Doubles", "Drills", "Match Play"], icon: "🎾" },
  "pickleball": { name: "Pickleball", desc: "America's fastest-growing sport. Easy to learn, hard to master — find beginners for friendly rallies or 5.0+ competitors for intense dinking battles.", skillSystem: "DUPR Rating (2.0–8.0)", formats: ["Singles", "Doubles", "Mixed Doubles", "Open Play", "Drill Sessions"], icon: "🏓" },
  "golf": { name: "Golf", desc: "From beginner 30+ handicappers to scratch players, find partners for casual rounds, stroke play competitions, or 9-hole quick matches.", skillSystem: "Handicap Index (WHS)", formats: ["Stroke Play", "Match Play", "9 Holes", "Practice Round"], icon: "⛳" },
  "table-tennis": { name: "Table Tennis", desc: "Club play, competitive league matches, or casual sessions — find USATT-rated opponents or beginners learning to serve.", skillSystem: "USATT Rating (500–3000+)", formats: ["Singles", "Doubles", "Practice Drills", "League Matches"], icon: "🏓" },
  "ski": { name: "Skiing", desc: "Plan ski weekends and powder days with compatible partners. From groomer cruisers to expert terrain hunters — match by ability, resort preference, and travel dates.", skillSystem: "Skill Level (Beginner to Expert)", formats: ["Weekend Trips", "Day Trips", "Lessons", "Terrain Specific"], icon: "⛷️" },
  "padel": { name: "Padel", desc: "Europe's explosive racket sport is now growing in North America. Find doubles partners for this fast-paced, accessible game.", skillSystem: "Skill Level (1–7)", formats: ["Doubles", "Competitive Matches", "Social Play"], icon: "🎾" },
  "squash": { name: "Squash", desc: "High-intensity matches in enclosed courts. Find rated opponents for league play or friendly hit-arounds.", skillSystem: "US Squash Rating", formats: ["Singles", "Match Play", "Drills", "Competitive Leagues"], icon: "🎯" },
  "badminton": { name: "Badminton", desc: "From casual backyard games to competitive shuttlers — find singles or doubles partners for this underrated, intense sport.", skillSystem: "Skill Level (Beginner to Advanced)", formats: ["Singles", "Doubles", "Mixed Doubles", "Drop-in Sessions"], icon: "🏸" },
  "climbing": { name: "Climbing", desc: "Find belay partners, bouldering crews, and outdoor crag partners. Match by grade (V0–V15 boulder, 5.8–5.15 rope), style, and location.", skillSystem: "V-Scale (Boulder) / YDS (Rope)", formats: ["Top Rope", "Lead Climbing", "Bouldering", "Outdoor Climbing"], icon: "🧗" },
};

const ALL_SPORTS = Object.keys(SPORTS_INFO);

/* ── WCAG compliant sport card (dark background, adequate contrast ratios) ── */
function SportCard({ sport, info, playerCount }) {
  return (
    <Link
      to={`/find-partners?sport=${sport}`}
      className="bg-[#161B28] border border-white/8 rounded-2xl p-5 hover:border-brand-green/40 hover:bg-[#1A2035] transition-all group dark-section"
      data-testid={`sport-directory-card-${sport}`}
      aria-label={`${info.name} — find partners`}
    >
      <div className="text-4xl mb-3" aria-hidden="true">{info.icon}</div>
      {/* text-white on dark bg → contrast ~14:1 ✓ WCAG AAA */}
      <h3 className="font-bold text-white text-lg mb-1 group-hover:text-brand-green transition-colors">{info.name}</h3>
      {/* text-brand-green on dark bg → contrast ~6.5:1 ✓ WCAG AA */}
      <p className="text-xs text-brand-green mb-2">
        {playerCount === undefined
          ? "Checking availability…"
          : playerCount.available === false
            ? "No venue here yet"
            : playerCount.venue_count > 0
              ? `${playerCount.venue_count} ${playerCount.venue_count === 1 ? "venue" : "venues"} · ${playerCount.players} ${playerCount.players === 1 ? "player" : "players"}`
              : `${playerCount.players} ${playerCount.players === 1 ? "player" : "players"}`}
      </p>
      {/* text-gray-300 on dark bg → contrast ~8:1 ✓ WCAG AA */}
      <p className="text-sm text-gray-300 mb-3">{info.desc}</p>
      <div className="text-xs text-gray-400 mb-3">
        <span className="font-medium text-gray-300">Skill System:</span> {info.skillSystem}
      </div>
      <div className="flex flex-wrap gap-1">
        {info.formats.slice(0, 3).map((f) => (
          <span key={f} className="text-xs px-1.5 py-0.5 bg-white/8 text-gray-300 border border-white/10 rounded">{f}</span>
        ))}
      </div>
      <div className="flex items-center gap-1 text-brand-green text-xs font-bold mt-4 group-hover:gap-2 transition-all">
        Find Partners <ArrowRight size={12} aria-hidden="true" />
      </div>
    </Link>
  );
}

export default function SportsDirectoryPage() {
  const { sport } = useParams();
  const [sportStats, setSportStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/sports`)
      .then((r) => setSportStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Returns the whole sport record, not just the count: /api/sports also reports
  // venue_count and whether the sport is playable here at all, and the card needs
  // both. Returning undefined lets the card show a neutral "checking" state
  // rather than inventing a figure for a sport the API did not return.
  const getPlayerCount = (sportId) => {
    return sportStats.find((x) => x.id === sportId);
  };

  const filtered = ALL_SPORTS.filter((s) =>
    !search || SPORTS_INFO[s].name.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Single sport detail page ─────────────────────────────────────────── */
  if (sport && SPORTS_INFO[sport]) {
    const info = SPORTS_INFO[sport];
    return (
      <div className="min-h-screen bg-[#0F1629] pt-16 dark-section">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link to="/sports" className="text-brand-green hover:underline text-sm mb-8 block">← All Sports</Link>

          <div className="text-6xl mb-4" aria-hidden="true">{info.icon}</div>
          {/* text-white on #0F1629 → contrast ~14:1 ✓ */}
          <h1 className="text-5xl font-heading font-black text-white mb-3">{info.name}</h1>
          {/* text-brand-green on #0F1629 → contrast ~6.5:1 ✓ */}
          <p className="text-brand-green text-sm font-medium mb-4">
            {(() => {
              const s = getPlayerCount(sport);
              if (!s) return "—";
              if (s.available === false) return "No venue mapped here yet";
              return `${s.venue_count} ${s.venue_count === 1 ? "venue" : "venues"} · ${s.players} active ${s.players === 1 ? "player" : "players"}`;
            })()}
          </p>
          {/* text-gray-300 on #0F1629 → contrast ~8:1 ✓ */}
          <p className="text-gray-300 leading-relaxed mb-8 max-w-2xl">{info.desc}</p>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Dark card with adequate contrast */}
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <h3 className="font-bold text-white mb-3">Skill Rating System</h3>
              <p className="text-sm text-gray-300">{info.skillSystem}</p>
            </div>
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <h3 className="font-bold text-white mb-3">Play Formats</h3>
              <div className="flex flex-wrap gap-1.5">
                {info.formats.map((f) => (
                  <span key={f} className="text-xs px-2 py-1 bg-white/8 text-gray-300 border border-white/10 rounded-lg">{f}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to={`/find-partners?sport=${sport}`}
              className="inline-flex items-center gap-2 px-8 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange/90 transition-colors"
              data-testid={`find-${sport}-partners-btn`}
            >
              Find {info.name} Partners <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link
              to="/group-sessions"
              className="inline-flex items-center gap-2 px-6 py-3 border border-brand-green/40 text-brand-green font-medium rounded-xl hover:bg-brand-green/10 transition-colors"
            >
              Group Sessions <ChevronRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ── Sports directory listing ─────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#0F1629] pt-16 dark-section">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero heading — dark bg for proper contrast */}
        <div className="text-center mb-10">
          {/* text-white on #0F1629 → ~14:1 ✓ WCAG AAA */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mb-4">
            Find Partners for Any Sport
          </h1>
          {/* text-gray-300 on #0F1629 → ~8:1 ✓ */}
          <p className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto">
            From tennis to climbing — discover players, schedule sessions, and grow your game.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-sm mx-auto mb-10 relative">
          <label htmlFor="sport-search" className="sr-only">Search sports</label>
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" aria-hidden="true" />
          <input
            id="sport-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sports…"
            className="w-full pl-10 pr-4 py-3 bg-[#1A2035] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green/40 dark-section"
          />
        </div>

        {/* Sport cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="list" aria-label="Sports directory">
          {filtered.map((s) => (
            <div key={s} role="listitem">
              <SportCard sport={s} info={SPORTS_INFO[s]} playerCount={getPlayerCount(s)} />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-3">Looking for a group session instead?</p>
          <Link
            to="/group-sessions"
            className="inline-flex items-center gap-2 text-brand-green font-semibold hover:underline"
          >
            Browse Open Sessions <ChevronRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}
